/**
 * AEVIA — Foundation JS
 *
 * Phase 1A scope only: no header/hero/mega-menu/carousel/quick-add logic
 * lives here yet (those ship with their own sections in Phase 1B, each
 * owning its own interaction code). This file only sets up:
 *   1. An AEVIA namespace other section scripts will attach to later.
 *   2. prefers-reduced-motion detection, wired to the .reduce-motion class
 *      already defined in aevia.css.
 *
 * UI Premium motion module (approved "AEVIA — UI PREMIUM MOTION +
 * BUTTONS"): adds only presentation timing on top of the existing
 * behaviors — exit animations for the mega-menu and mobile drawer
 * (the element stays visible for the exit duration, then [hidden] is
 * set exactly as before), a 300ms accordion height animation, and a
 * one-shot IntersectionObserver scroll reveal. Every aria-expanded
 * update, focus move, Escape/outside-click/focus-trap behavior is
 * unchanged and happens at the same moment as before. With reduced
 * motion (or without Web Animations / IntersectionObserver support)
 * everything falls back to the original instant toggles.
 *
 * No framework, no jQuery, no external libraries.
 */
(function () {
  "use strict";

  window.AEVIA = window.AEVIA || {};

  /**
   * Mirrors the user's prefers-reduced-motion setting onto <html> as a
   * class, and keeps it in sync if the setting changes mid-session.
   */
  function initReducedMotion() {
    var query = window.matchMedia("(prefers-reduced-motion: reduce)");
    var root = document.documentElement;

    function applyState(matches) {
      root.classList.toggle("reduce-motion", matches);
    }

    applyState(query.matches);

    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", function (event) {
        applyState(event.matches);
      });
    } else if (typeof query.addListener === "function") {
      // Safari < 14 fallback
      query.addListener(function (event) {
        applyState(event.matches);
      });
    }
  }

  /* ---------------------------------------------------------------
   * Motion helpers (UI Premium module)
   * ------------------------------------------------------------- */

  var MOTION = {
    menu: 250,
    drawer: 300,
    accordion: 300,
    easeOut: "cubic-bezier(0.22, 1, 0.36, 1)",
    ease: "cubic-bezier(0.4, 0, 0.2, 1)"
  };

  function prefersReducedMotion() {
    if (document.documentElement.classList.contains("reduce-motion")) return true;
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  /**
   * Plays the CSS exit animation bound to `closingClass`, then sets
   * [hidden] — the same final state the original code set immediately.
   * A later reopen cancels a pending exit (see cancelExit).
   */
  function hideWithExit(el, closingClass, duration) {
    cancelExit(el, closingClass);
    if (el.hidden) return;
    if (prefersReducedMotion()) {
      el.hidden = true;
      return;
    }
    el.classList.add(closingClass);
    el._aeviaExitTimer = window.setTimeout(function () {
      el._aeviaExitTimer = null;
      el.classList.remove(closingClass);
      el.hidden = true;
    }, duration);
  }

  function cancelExit(el, closingClass) {
    if (el._aeviaExitTimer) {
      window.clearTimeout(el._aeviaExitTimer);
      el._aeviaExitTimer = null;
    }
    el.classList.remove(closingClass);
  }

  /**
   * Returns the focusable elements inside a container, in DOM order.
   * Used by the mobile drawer's focus trap.
   */
  function getFocusable(container) {
    var selector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(container.querySelectorAll(selector))
      .filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
  }

  /**
   * Sticky header: adds .is-scrolled once the page has scrolled past a
   * small threshold, purely for the subtle border/shadow defined in
   * aevia.css. No layout dependency on a hero section.
   */
  function initHeaderScrollState() {
    var header = document.querySelector("[data-aevia-header]");
    if (!header) return;

    var THRESHOLD = 4;
    var ticking = false;

    function update() {
      header.classList.toggle("is-scrolled", window.scrollY > THRESHOLD);
      ticking = false;
    }

    window.addEventListener(
      "scroll",
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

    update();
  }

  /**
   * Shop mega-menu (desktop). Toggled by click/Enter/Space on its
   * trigger button — never hover-only, per accessibility requirement.
   * Closes on Escape (focus returns to the trigger) and on an outside
   * click.
   */
  function initMegaMenu() {
    var trigger = document.getElementById("AEVIAShopMenuTrigger");
    var panel = document.getElementById("AEVIAShopMegaMenu");
    if (!trigger || !panel) return;

    function open() {
      cancelExit(panel, "is-closing");
      panel.hidden = false;
      trigger.setAttribute("aria-expanded", "true");
      document.addEventListener("click", handleOutsideClick, true);
      document.addEventListener("keydown", handleKeydown);
    }

    function close(options) {
      hideWithExit(panel, "is-closing", MOTION.menu);
      trigger.setAttribute("aria-expanded", "false");
      document.removeEventListener("click", handleOutsideClick, true);
      document.removeEventListener("keydown", handleKeydown);
      if (options && options.returnFocus) {
        trigger.focus();
      }
    }

    function handleOutsideClick(event) {
      if (!panel.contains(event.target) && event.target !== trigger) {
        close({ returnFocus: false });
      }
    }

    function handleKeydown(event) {
      if (event.key === "Escape") {
        close({ returnFocus: true });
      }
    }

    trigger.addEventListener("click", function () {
      var isOpen = trigger.getAttribute("aria-expanded") === "true";
      if (isOpen) {
        close({ returnFocus: false });
      } else {
        open();
      }
    });
  }

  /**
   * Mobile drawer: open/close, focus trap, Escape, backdrop click.
   * Focus returns to the hamburger button on close.
   */
  function initMobileDrawer() {
    var trigger = document.getElementById("AEVIAMobileMenuTrigger");
    var drawer = document.getElementById("AEVIAMobileMenuDrawer");
    var closeButton = document.getElementById("AEVIAMobileMenuClose");
    var backdrop = drawer
      ? drawer.querySelector("[data-aevia-drawer-backdrop]")
      : null;

    if (!trigger || !drawer || !closeButton) return;

    var lastFocused = null;

    function handleKeydown(event) {
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key !== "Tab") return;

      var focusable = getFocusable(drawer.querySelector(".aevia-mobile-drawer__panel"));
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function open() {
      lastFocused = document.activeElement;
      cancelExit(drawer, "is-closing");
      drawer.hidden = false;
      trigger.setAttribute("aria-expanded", "true");
      document.body.style.overflow = "hidden";
      closeButton.focus();
      document.addEventListener("keydown", handleKeydown);
    }

    function close() {
      hideWithExit(drawer, "is-closing", MOTION.drawer);
      trigger.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeydown);
      if (lastFocused) {
        lastFocused.focus();
      } else {
        trigger.focus();
      }
    }

    trigger.addEventListener("click", open);
    closeButton.addEventListener("click", close);
    if (backdrop) {
      backdrop.addEventListener("click", close);
    }
  }

  /**
   * Generic accordion disclosure (used here for "Shop" inside the
   * mobile drawer; the same markup/behavior is reused by the Footer
   * in a later phase).
   */
  function initAccordions() {
    var triggers = document.querySelectorAll(".aevia-accordion__trigger");

    triggers.forEach(function (trigger) {
      var panelId = trigger.getAttribute("aria-controls");
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;

      trigger.addEventListener("click", function () {
        var isOpen = trigger.getAttribute("aria-expanded") === "true";
        trigger.setAttribute("aria-expanded", String(!isOpen));
        togglePanel(panel, !isOpen);
      });
    });
  }

  /**
   * Opens/closes an accordion panel. The final state is exactly the
   * original one (panel.hidden = !open); in between, a 300ms height +
   * opacity animation runs when motion is allowed and the Web
   * Animations API exists.
   */
  function togglePanel(panel, open) {
    var startHeight = null;
    if (panel._aeviaAnimation) {
      startHeight = panel.getBoundingClientRect().height;
      panel._aeviaAnimation.cancel();
      panel._aeviaAnimation = null;
    }

    if (prefersReducedMotion() || typeof panel.animate !== "function") {
      clearPanelStyles(panel);
      panel.hidden = !open;
      return;
    }

    panel.hidden = false;
    clearPanelStyles(panel);
    var cs = window.getComputedStyle(panel);
    var fullHeight = panel.getBoundingClientRect().height;
    var from = startHeight !== null ? startHeight : open ? 0 : fullHeight;
    var to = open ? fullHeight : 0;

    var closedFrame = { height: "0px", paddingTop: "0px", paddingBottom: "0px", opacity: 0 };
    var openFrame = { height: fullHeight + "px", paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, opacity: 1 };
    var startFrame = from === 0 ? closedFrame : from === fullHeight ? openFrame : { height: from + "px", opacity: from / (fullHeight || 1) };
    var endFrame = to === 0 ? closedFrame : openFrame;

    panel.style.overflow = "hidden";
    var animation = panel.animate([startFrame, endFrame], {
      duration: MOTION.accordion,
      easing: open ? MOTION.easeOut : MOTION.ease,
      // closing holds the collapsed frame until [hidden] is set, so the
      // panel never flashes back to full height for one frame
      fill: open ? "none" : "forwards"
    });
    panel._aeviaAnimation = animation;

    animation.onfinish = function () {
      if (panel._aeviaAnimation !== animation) return;
      panel._aeviaAnimation = null;
      clearPanelStyles(panel);
      panel.hidden = !open;
      if (!open) animation.cancel(); // drop the held frame once hidden
    };
  }

  function clearPanelStyles(panel) {
    panel.style.overflow = "";
  }

  /**
   * One-shot scroll reveal for sections and vertical-grid cards. Only
   * elements that are rendered AND below the fold at load are tagged
   * (so nothing on screen ever blinks), each is revealed once and then
   * unobserved. Hair Advisor content is excluded (it has its own CSS
   * result reveal). Skipped entirely with reduced motion or without
   * IntersectionObserver.
   */
  function initReveal() {
    if (prefersReducedMotion() || !("IntersectionObserver" in window)) return;

    var selectors = [
      ".aevia-brand-philosophy",
      ".aevia-brand-spotlight",
      ".aevia-category-grid",
      ".aevia-product-grid",
      ".aevia-hair-advisor",
      ".aevia-routine",
      ".aevia-treatments",
      ".aevia-journal",
      ".aevia-newsletter",
      ".aevia-product-page__advisor-strip",
      ".aevia-product-page__related",
      ".aevia-collection-page__grid > *",
      ".aevia-search-page__grid > *",
      ".aevia-tyrrel-page__intro",
      ".aevia-tyrrel-page__image",
      ".aevia-tyrrel-page__products-title",
      ".aevia-tyrrel-page__grid > *",
      ".aevia-tyrrel-page__lines-list",
      ".aevia-tyrrel-page__ctas",
      ".aevia-chi-siamo-page__body",
      ".aevia-chi-siamo-page__ctas"
    ];

    var candidates = Array.prototype.slice.call(document.querySelectorAll(selectors.join(",")));
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    var targets = candidates.filter(function (el) {
      if (el.closest("[data-aevia-hair-advisor]")) return false;
      if (el.getClientRects().length === 0) return false; // not rendered (hidden)
      if (el.getBoundingClientRect().top < viewportHeight) return false; // already on screen
      return true;
    });

    // keep only the outermost target of any nested pair
    targets = targets.filter(function (el) {
      return !targets.some(function (other) {
        return other !== el && other.contains(el);
      });
    });

    if (!targets.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );

    targets.forEach(function (el) {
      el.classList.add("aevia-reveal");
      observer.observe(el);
    });
  }

  function init() {
    document.documentElement.classList.remove("no-js");
    document.documentElement.classList.add("js");
    initReducedMotion();
    initHeaderScrollState();
    initMegaMenu();
    initMobileDrawer();
    initAccordions();
    initReveal();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();